// Copyright TraderEvolution Global LTD. © 2017-2024. All rights reserved.
import { MAMode } from '../IndicatorConstants';
import { IndicatorScriptBase } from '../IndicatorScriptBase';
import { type iBuildInIndicator } from '../iBuildInIndicator';
import { IndicatorFunctions } from '../IndicatorFunctions';
import { ExpandDoubleVector } from '../DoubleMatrix';
import { Color } from '../../../Graphics';
import { LineStyle } from '../IndicatorScriptBaseEnums';
import { InputParameterInteger } from '../InputParamaterClasses/InputParameterInteger';

export class TEMA extends IndicatorScriptBase {
    constructor () {
        super();
        this.ProjectName = 'Triple EMA';
        this.Comments = 'Triple EMA';
        super.SetIndicatorLine('tema', Color.Red, 2, LineStyle.SimpleChart);
        this.SeparateWindow = false;

        this.PeriodMA = 9;
        super.InputParameter(new InputParameterInteger('PeriodMA', 'Period of MA', 0, 1, 9999));

        this.smaSeries = new ExpandDoubleVector();
        this.sma1Series = new ExpandDoubleVector();
        this.sma2Series = new ExpandDoubleVector();
    }

    public PeriodMA: number;

    private sma: iBuildInIndicator;

    private smaSeries: ExpandDoubleVector;

    private sma1Series: ExpandDoubleVector;

    private sma2Series: ExpandDoubleVector;

    public Init (): void {
        super.Init();
        super.IndicatorShortName(this.GetIndicatorShortName());
    }

    public override GetIndicatorShortName (): string {
        return 'TEMA(' + this.PeriodMA + ')';
    }

    public NextBar (): void {
        this.smaSeries.Add(0.0);
        this.sma1Series.Add(0.0);
        this.sma2Series.Add(0.0);
        super.NextBar();
    }

    public override createInternalIndicators (): never[] {
        this.sma = this.Indicators.iMA(this.CurrentData, this.PeriodMA, MAMode.EMA);

        super.IndicatorShortName(this.GetIndicatorShortName());

        return [this.sma] as never;
    }

    public OnQuote (): void {
        super.OnQuote();

        this.setSMA();
        this.setSMA1();
        this.setSMA2();

        super.SetValue(0, 0, this.getTema());
    }

    private setSMA (): void {
        this.smaSeries[this.smaSeries.Length - 1] = this.sma.getValue();
    }

    private setSMA1 (): void {
        const result = IndicatorFunctions.CallMovingFunction(MAMode.EMA, this.smaSeries, this.PeriodMA, 1);
        this.sma1Series[this.sma1Series.Length - 1] = Number.isNaN(result) ? 0 : result;
    }

    private setSMA2 (): void {
        const result = IndicatorFunctions.CallMovingFunction(MAMode.EMA, this.sma1Series, this.PeriodMA, 1);
        this.sma2Series[this.sma2Series.Length - 1] = Number.isNaN(result) ? 0 : result;
    }

    private getTema (): number {
        const result = 3 * this.smaSeries[this.smaSeries.Length - 1] - 3 * this.sma1Series[this.sma1Series.Length - 1] + this.sma2Series[this.sma2Series.Length - 1];
        return result;
    }
}
